import { calculateArea, totalDistance } from "@nova/calculate-geospatial"
import { formatArea, formatDistance } from "@nova/format"
import * as Icons from "@nova/component-icons"
import {
    AreaUnit,
    DistanceUnit,
    GeometryType,
    LayerIndex,
    SidePanelLayerType,
    ToolName,
} from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { Marker } from "react-map-gl"
import { useEffect, useMemo, useState } from "react"
import { LngLatCoordinatePair } from "features/map/event/types"
import { Line } from "@nova/protobuf"
import {
    selectWorkspaceAreaUnit,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    selectAllLineBoundingBoxes,
    selectAllLines,
    selectLineById,
} from "features/entity-caching/lines/selectors"
import { getGenericMockedInspectorPanelBody } from "features/map/inspector-panel/inspector-panels/bodies/GenericInspectorPanelBody"
import { getGenericDuplicatableFooterActions } from "features/map/inspector-panel/inspector-panels/footers/GenericInspectorPanelFooter"
import {
    getGenericMockableInspectorPanel,
    getGenericMockedInspectorPanelComponent,
} from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { getGenericMockedInspectorPanelHeader } from "features/map/inspector-panel/inspector-panels/headers/GenericInspectorPanelHeader"
import { InspectorPanelLineLayer } from "features/map/inspector-panel/lines/layer/InspectorPanelLineLayer"
import { duplicateLine, zoomToLine } from "features/map/inspector-panel/lines/module"
import { deleteLine, updateLine } from "features/pubsub/entityActions/cubes/line"
import { PluginRegistry } from "plugins/plugin-registry"
import { InspectorPanelLineMockedFooter } from "features/map/inspector-panel/lines/InspectorPanelLineMockedFooter"
import { getInspectorPanelLine } from "features/map/inspector-panel/lines/InspectorPanelLine"
import { useNormalLineLayer, useEditingLineLayer } from "./useLineLayer"
import { selectLineEditingData } from "./LineSlice"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { resolveEventLngLat } from "plugins/resolveLngLat"
import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"

export function registerLinePlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "lines",
            displayName: "Line",
            mockedDisplayName: "New Line",
            sidePanelLayerType: SidePanelLayerType.Line,
            PrimaryIcon: Icons.Polyline,
            updateAction: updateLine,
            deleteAction: deleteLine,
            confirmDelete: () => false,
            protobufMessage: Line,
            selectAllEntities: selectAllLines,
            selectSingleEntity: selectLineById,
            zoomTo: zoomToLine,
            duplicate: duplicateLine,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin")
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericMockableInspectorPanel({
                NonMocked: getInspectorPanelLine(spec),
                Mocked: getGenericMockedInspectorPanelComponent({
                    Header: getGenericMockedInspectorPanelHeader(spec),
                    Body: getGenericMockedInspectorPanelBody({
                        RenderComponent: InspectorPanelLineLayer,
                    }),
                    Footer: InspectorPanelLineMockedFooter,
                }),
            }),
            useFooterActions: getGenericDuplicatableFooterActions(spec),
            useSubtitles,
        }))
        .implement("bounding-box-data-provider", () => ({
            useBoundingBoxData: () => useAppSelector(selectAllLineBoundingBoxes),
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: () => useNormalLineLayer(),
            zIndex: LayerIndex.Line,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: () => useEditingLineLayer(),
            zIndex: LayerIndex.GeometryBeingEdited,
        }))
        .implement("toolbox-plugin", () => ({
            name: ToolName.Line,
            shortcut: { letters: ["L"] },
        }))
        .implement("map-modal-plugin", () => ({
            Modal: () => {
                const editingData = useAppSelector(selectLineEditingData)
                const points = geometryDataStore.useGeometryData("points")
                const [coords, setCoords] = useState<LngLatCoordinatePair | undefined>()
                const sidePanelWidth = useResizableSidePanelWidth()
                useEffect(() => {
                    const listener = (event: MouseEvent) => {
                        if (points.length || !editingData) return
                        if ((event.target as HTMLElement)?.className === "maplibregl-canvas") {
                            const coordinatePair = resolveEventLngLat(event)
                            setCoords([
                                coordinatePair.longitude,
                                coordinatePair.latitude,
                            ] as LngLatCoordinatePair)
                        } else {
                            setCoords(undefined)
                        }
                    }
                    window.addEventListener("mousemove", listener)
                    return () => {
                        window.removeEventListener("mousemove", listener)
                    }
                }, [editingData, points, sidePanelWidth])

                return (
                    <>
                        {editingData && !points.length && coords ? (
                            <Marker
                                longitude={coords[0]}
                                latitude={coords[1]}
                                anchor={"top-left"}
                                offset={[12, 16]}
                                style={{ pointerEvents: "none" }}
                            >
                                <div className="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                                    <small>Left click to start line</small>
                                </div>
                            </Marker>
                        ) : null}
                    </>
                )
            },
        }))
}

function useSubtitles(id: string) {
    const line = useAppSelector(selectLineById(id))
    const organizationSettingDistance = useAppSelector(selectWorkspaceDistanceUnit)
    const organizationSettingArea = useAppSelector(selectWorkspaceAreaUnit)
    const [lineDistance, lineArea] = useMemo(() => {
        const distance = totalDistance(line.coordinates, line.geometryType === GeometryType.Polygon)
        const formattedDistance =
            formatDistance({
                value: distance,
                unit: organizationSettingDistance as DistanceUnit,
            }) +
            " " +
            (line.geometryType === GeometryType.Polygon ? "perimeter" : "length")
        const geospatialCoords = line.coordinates
        const area =
            line.coordinates.length > 2 && line.geometryType === GeometryType.Polygon
                ? calculateArea(geospatialCoords)
                : undefined
        const formattedArea = area
            ? formatArea({ value: area, unit: organizationSettingArea as AreaUnit }) + " area"
            : undefined
        return [formattedDistance, formattedArea]
    }, [line, organizationSettingArea, organizationSettingDistance])
    return [lineArea, lineDistance].filter((s): s is string => !!s)
}
